import type { Extension } from '@codemirror/state';
import type { MaybeRefOrGetter, Ref } from 'vue';
import { defaultKeymap, history, historyKeymap, indentWithTab } from '@codemirror/commands';
import { Compartment, EditorState, Transaction } from '@codemirror/state';
import { EditorView, keymap, placeholder as placeholderExt } from '@codemirror/view';
import { computed, onBeforeUnmount, onMounted, shallowRef, toValue, watch } from 'vue';

export function useCodeMirror(
	container: MaybeRefOrGetter<HTMLElement | null | undefined>,
	options: {
		modelValue: Ref<string>;
		extensions: MaybeRefOrGetter<Extension[]>;
		placeholder?: MaybeRefOrGetter<string | undefined>;
		tabSize?: number;
		disabled?: MaybeRefOrGetter<boolean | undefined>;
		readonly?: MaybeRefOrGetter<boolean | undefined>;
		inert?: MaybeRefOrGetter<boolean | undefined>;
		lineWrapping?: MaybeRefOrGetter<boolean | undefined>;
	},
) {
	const view = shallowRef<EditorView>();

	const dynamicExtensions = new Compartment();
	const editableConf = new Compartment();
	const readonlyConf = new Compartment();
	const placeholderConf = new Compartment();
	const lineWrappingConf = new Compartment();

	const isInert = computed(() => toValue(options.inert) ?? false);
	const isDisabled = computed(() => toValue(options.disabled) ?? false);
	const isEditable = computed(() => !isDisabled.value && !isInert.value);
	const isReadonly = computed(() => (toValue(options.readonly) ?? false) || isDisabled.value || isInert.value);
	const isFocusable = computed(() => !isDisabled.value && !isInert.value);
	const isLineWrapping = computed(() => toValue(options.lineWrapping) ?? true);

	onMounted(() => {
		const el = toValue(container);
		if (!el) return;

		const startState = EditorState.create({
			doc: options.modelValue.value,
			extensions: [
				history(),
				keymap.of([...defaultKeymap, ...historyKeymap, indentWithTab]),
				lineWrappingConf.of(isLineWrapping.value ? EditorView.lineWrapping : []),
				EditorState.tabSize.of(options.tabSize ?? 4),
				editableConf.of(EditorView.editable.of(isEditable.value)),
				readonlyConf.of(EditorState.readOnly.of(isReadonly.value)),
				placeholderConf.of(toValue(options.placeholder) ? placeholderExt(toValue(options.placeholder)!) : []),
				dynamicExtensions.of(toValue(options.extensions)),
				EditorView.updateListener.of((update) => {
					if (update.docChanged) {
						options.modelValue.value = update.state.doc.toString();
					}
				}),
			],
		});

		view.value = new EditorView({
			state: startState,
			parent: el,
		});
	});

	onBeforeUnmount(() => {
		view.value?.destroy();
	});

	// Move the existing view if the container element is swapped out (e.g. the
	// editor is re-parented between wrapped/unwrapped layouts) instead of orphaning it.
	watch(() => toValue(container), (el) => {
		if (el && view.value && view.value.dom.parentElement !== el) {
			el.append(view.value.dom);
		}
	});

	watch(options.modelValue, (val) => {
		if (!view.value || view.value.state.doc.toString() === val) return;

		view.value.dispatch({
			changes: { from: 0, to: view.value.state.doc.length, insert: val },
			annotations: Transaction.addToHistory.of(false),
		});
	});

	watch(() => toValue(options.extensions), (exts) => {
		if (!view.value) return;
		view.value.dispatch({ effects: dynamicExtensions.reconfigure(exts) });
	});

	watch([isEditable, isReadonly], () => {
		if (!view.value) return;

		view.value.dispatch({
			effects: [
				editableConf.reconfigure(EditorView.editable.of(isEditable.value)),
				readonlyConf.reconfigure(EditorState.readOnly.of(isReadonly.value)),
			],
		});

		if (!isFocusable.value) {
			view.value.contentDOM.blur();
		}
	});

	watch(() => toValue(options.placeholder), (val) => {
		if (!view.value) return;
		view.value.dispatch({ effects: placeholderConf.reconfigure(val ? placeholderExt(val) : []) });
	});

	watch(isLineWrapping, (wrap) => {
		if (!view.value) return;
		view.value.dispatch({ effects: lineWrappingConf.reconfigure(wrap ? EditorView.lineWrapping : []) });
	});

	function focus() {
		if (!isFocusable.value) return;
		view.value?.focus();
	}

	return { view, focus };
}
